import { WAIT_INTERRUPTS, WAIT_UNITS, type WaitInterrupt, type WaitUnit } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Checkbox, Segmented, Select } from "@sagot/ui";
import { useId } from "react";
import { useWorkspaceDetails } from "../../../../session/workspace-context";
import { IntegerInput } from "../../../workspace/shared";
import type { StepDuration, WaitStep } from "../types";
import type { FlowContext } from "./flow-context";
import { PathSelect } from "./path-select";
import { capLabel, maxForUnit, waitCapMinutes, waitExceedsPlan, waitModeOf, withWaitMode, WAIT_MODES, type WaitMode } from "./wait-model";
import { type FormProps } from "./webhook-form";
import { SwitchRow } from "./switch-row";

const F = "engage.workflows.fields";

interface LengthProps {
  duration: StepDuration;
  disabled: boolean;
  onChange: (duration: StepDuration) => void;
}

/** The length of a wait for a set time (an amount and a unit), held to what the plan allows. */
function LengthFields({ duration, disabled, onChange }: LengthProps) {
  const { t } = useTranslation();
  const plan = useWorkspaceDetails().data?.plan;
  const cap = capLabel(waitCapMinutes(plan));
  const tooLong = waitExceedsPlan(duration, plan);
  const set = (patch: Partial<StepDuration>) => { onChange({ ...duration, ...patch }); };
  return (
    <fieldset className="space-y-1.5">
      <legend className="label">{t(`${F}.waitFor`)}</legend>
      <div className="flex gap-2">
        <IntegerInput aria-label={t(`${F}.waitValue`)} className="w-28" min={1} max={maxForUnit(duration.unit, plan)} disabled={disabled} value={duration.value} onChange={(value) => { set({ value }); }} />
        <Select aria-label={t(`${F}.waitUnit`)} className="w-36" disabled={disabled} value={duration.unit} onChange={(e) => { set({ unit: e.target.value as WaitUnit }); }}>
          {WAIT_UNITS.map((unit) => <option key={unit} value={unit}>{t(`${F}.waitUnits.${unit}`)}</option>)}
        </Select>
      </div>
      <p className={tooLong ? "text-xs font-semibold text-danger" : "hint"} role={tooLong ? "alert" : undefined}>
        {t(tooLong ? `${F}.waitTooLong` : `${F}.waitCap`, { value: cap.value, unit: t(`${F}.waitUnits.${cap.unit}`) })}
      </p>
    </fieldset>
  );
}

/** Wait: a set time (optionally then until the office is open) or until the office opens, and what interrupts it. */
export function WaitForm({ step, disabled, onChange, paths, canAddPath }: FormProps<WaitStep> & Pick<FlowContext, "paths" | "canAddPath">) {
  const { t } = useTranslation();
  const plan = useWorkspaceDetails().data?.plan;
  const id = useId();
  const mode = waitModeOf(step);
  const cap = capLabel(waitCapMinutes(plan));
  const toggle = (interrupt: WaitInterrupt, on: boolean) => {
    const rest = step.interrupt_on.filter((item) => item !== interrupt);
    onChange({ ...step, interrupt_on: on ? WAIT_INTERRUPTS.filter((item) => item === interrupt || rest.includes(item)) : rest });
  };
  return (
    <div className="space-y-5">
      <div>
        <span className="label">{t(`${F}.waitMode`)}</span>
        <Segmented
          aria-label={t(`${F}.waitMode`)}
          value={mode}
          onValueChange={(next: WaitMode) => { if (next !== mode) onChange(withWaitMode(step, next)); }}
          options={WAIT_MODES.map((option) => ({ value: option, label: t(`${F}.waitModes.${option}`) }))}
        />
      </div>
      {step.duration ? (
        <>
          <LengthFields duration={step.duration} disabled={disabled} onChange={(duration) => { onChange({ ...step, duration }); }} />
          <SwitchRow label={t(`${F}.untilOffice`)} description={t(`${F}.untilOfficeHint`)} checked={step.until_office_hours === true} disabled={disabled} onChange={(on) => { onChange({ ...step, until_office_hours: on || undefined }); }} />
        </>
      ) : (
        <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted">{t(`${F}.waitOfficeHint`, { value: cap.value, unit: t(`${F}.waitUnits.${cap.unit}`) })}</p>
      )}
      <fieldset className="space-y-2">
        <legend className="label">{t(`${F}.interruptOn`)}</legend>
        {WAIT_INTERRUPTS.map((interrupt) => (
          <Checkbox key={interrupt} id={`${id}-${interrupt}`} label={t(`${F}.interrupts.${interrupt}`)} disabled={disabled} checked={step.interrupt_on.includes(interrupt)} onCheckedChange={(checked) => { toggle(interrupt, checked === true); }} />
        ))}
        <p className="hint">{t(`${F}.interruptHint`)}</p>
      </fieldset>
      {step.interrupt_on.length > 0 ? (
        <PathSelect label={t(`${F}.interruptedGoTo`)} hint={t(`${F}.interruptedHint`)} value={step.interrupted_goto} paths={paths} canAddPath={canAddPath} emptyLabel={t(`${F}.endHere`)} disabled={disabled} onChange={(interrupted_goto) => { onChange({ ...step, interrupted_goto }); }} />
      ) : null}
    </div>
  );
}
